/**
 * RADERNA. Fyra former, och de är inte utbytbara.
 *
 *   <Listrad>     `.trow`   sak till vänster, TAL till höger. Tidrader,
 *                           milrader, utläggsrader. Talet står i --mono med
 *                           tabular-nums, så en lista står i pelare.
 *   <Personrad>   `.person` initialer, namn, underrad, tal, åtgärd. Attest,
 *                           Laget idag, Inhyrda.
 *   <Orderkort>   `.ocard`  ett helt kort per arbetsorder, inte en rad.
 *   <Kryssrad>    `.moment` en ruta att bocka i. Moment och att-göra.
 *
 * ALLA TAL GÅR IN FÄRDIGFORMATERADE. Komponenterna får aldrig ett rakt tal ur
 * kontraktet: 450 är minuter och 129500 är ören, och ingen av dem är text en
 * människa ska läsa. Formatera i skärmen (7,5 h, 1 295 kr) och skicka in
 * strängen.
 */

import type { ReactNode } from 'react';
import { Ikon } from './Ikon';
import type { Ikonnamn } from './Ikon';

/* == Listrad ============================================================= */

export interface Listradprops {
  readonly titel: ReactNode;
  /** Liten grå text på SAMMA rad som titeln ("2 sep · egen bil"). */
  readonly bi?: ReactNode;
  /** Liten grå text på EGEN rad under ("Vidarefaktureras kunden"). */
  readonly under?: ReactNode;
  /** Talet till höger. Färdigformaterat. */
  readonly varde?: ReactNode;
  /** Finns den blir raden en knapp. */
  readonly onClick?: () => void;
}

export const Listrad = ({ titel, bi, under, varde, onClick }: Listradprops) => {
  const innehall = (
    <>
      <span>
        {titel}
        {bi !== undefined && <span className="ty"> {bi}</span>}
        {under !== undefined && <span className="sub2">{under}</span>}
      </span>
      {varde !== undefined && <span className="hh">{varde}</span>}
    </>
  );
  return onClick === undefined ? (
    <div className="trow">{innehall}</div>
  ) : (
    <button type="button" className="trow" onClick={onClick}>
      {innehall}
    </button>
  );
};

/**
 * `.forslag` — en rad som FÖRESLÅR något och har en knapp som lägger till det.
 * Milskärmens "Hem -> Tallmovägen 22 · 4,5 mil · [Lägg till]".
 *
 * BESLUT 08: milen fylls i FÖR HAND. Raden är ett förslag räknat ur adresser
 * appen redan har — ingen sparar var någon är, och ingenting läggs till förrän
 * användaren tryckt.
 */
export const Forslagsrad = ({
  titel,
  varde,
  atgard,
}: {
  readonly titel: ReactNode;
  readonly varde: ReactNode;
  readonly atgard: ReactNode;
}) => (
  <div className="forslag">
    <span>{titel}</span>
    <span className="st">{varde}</span>
    {atgard}
  </div>
);

/** `.mrow` — den tätare raden för materiallistor på arbetsordern. */
export const Materialrad = ({
  titel,
  varde,
}: {
  readonly titel: ReactNode;
  readonly varde: ReactNode;
}) => (
  <div className="mrow">
    <span>{titel}</span>
    <span>{varde}</span>
  </div>
);

/**
 * `.frad` — fakturaunderlagets rad. `total` ger den tjocka linjen och --display.
 *
 * BESLUT 05: attesterat och oattesterat redovisas SKILT och får ingen gemensam
 * total. En `total`-rad hör därför hemma inuti VAR sektion, aldrig under båda.
 */
export const Fakturarad = ({
  titel,
  varde,
  total = false,
}: {
  readonly titel: ReactNode;
  readonly varde: ReactNode;
  readonly total?: boolean;
}) => (
  <div className={total ? 'frad tot' : 'frad'}>
    <span>{titel}</span>
    <span>{varde}</span>
  </div>
);

/* == Personrad =========================================================== */

export interface Personradprops {
  /** Två bokstäver. Bygg med `initialer(namn)` nedan. */
  readonly initialer: string;
  readonly namn: ReactNode;
  readonly under?: ReactNode;
  /** Klockslag e.d. — mindre och dämpat (`.tm`). */
  readonly tid?: ReactNode;
  /** Timmar e.d. — större och halvfett (`.hh`). */
  readonly varde?: ReactNode;
  /** Oftast <Knapp variant="piller">Godkänn</Knapp>. */
  readonly atgard?: ReactNode;
}

export const Personrad = ({
  initialer,
  namn,
  under,
  tid,
  varde,
  atgard,
}: Personradprops) => (
  <div className="person">
    <span className="av" aria-hidden="true">
      {initialer}
    </span>
    <span className="nm">
      {namn}
      {under !== undefined && <small>{under}</small>}
    </span>
    {tid !== undefined && <span className="tm">{tid}</span>}
    {varde !== undefined && <span className="hh">{varde}</span>}
    {atgard}
  </div>
);

/** "Anton Ek" -> "AE". Tomt namn ger "?" i stället för en tom cirkel. */
export const initialer = (namn: string): string => {
  const ord = namn.trim().split(/\s+/).filter((o) => o !== '');
  if (ord.length === 0) return '?';
  const forsta = ord[0]?.[0] ?? '';
  const sista = ord.length > 1 ? ord[ord.length - 1]?.[0] ?? '' : '';
  return (forsta + sista).toUpperCase();
};

/* == Orderkort =========================================================== */

export interface Orderkortprops {
  /** Adressen. Det är så en hantverkare känner igen ett jobb. */
  readonly adress: ReactNode;
  /** "Lgh 1102 · Enskede · Vattenutkastare" */
  readonly meta?: ReactNode;
  /** "AO-2026-0001 · 08:00 · Privatkund" — i --mono. */
  readonly fot?: ReactNode;
  /** <Bricka> uppe till höger. */
  readonly bricka?: ReactNode;
  readonly onClick?: () => void;
}

export const Orderkort = ({ adress, meta, fot, bricka, onClick }: Orderkortprops) => (
  <button
    type="button"
    className="ocard"
    {...(onClick === undefined ? {} : { onClick })}
  >
    <span className="rowtop">
      <span className="adr">{adress}</span>
      {bricka}
    </span>
    {meta !== undefined && <span className="meta">{meta}</span>}
    {fot !== undefined && <span className="foot">{fot}</span>}
  </button>
);

/* == Kryssrad ============================================================ */

export interface Kryssradprops {
  readonly ikryssad: boolean;
  readonly onToggle: () => void;
  readonly children: ReactNode;
  /** Talet till höger — timmarna på ett moment. Blir tydligare när det kryssas i. */
  readonly varde?: ReactNode;
  /**
   * `todo` = att-göra-lista (överstruken text när den är klar).
   * `moment` = momentlista med linje mellan raderna.
   */
  readonly form?: 'moment' | 'todo';
  readonly disabled?: boolean;
}

/**
 * MOMENTEN HAMNAR ORDAGRANT PÅ KUNDENS FAKTURA. Det är därför de finns och
 * därför de är obligatoriska mot privatkund — texten användaren kryssar i är
 * texten kunden läser. Ändra den aldrig i gränssnittet.
 */
export const Kryssrad = ({
  ikryssad,
  onToggle,
  children,
  varde,
  form = 'moment',
  disabled = false,
}: Kryssradprops) => (
  <button
    type="button"
    className={form}
    aria-pressed={ikryssad}
    onClick={onToggle}
    disabled={disabled}
  >
    <span className="box">
      <Ikon namn="bock" storlek={15} tjocklek={3.2} />
    </span>
    <span className="tx">{children}</span>
    {varde !== undefined && <span className="mh">{varde}</span>}
  </button>
);

/* == Foton =============================================================== */

/**
 * `.photos` — rutnätet med bilder på arbetsordern.
 *
 * BESLUT 13: ingen kvitto-AI. Bilden är en bild. object_key genereras på
 * KLIENTEN när kortet tas (granska.nyObjectKey), fotoraden läggs i utkorgen
 * direkt och bytesen laddas upp när nätet kommer tillbaka — det är det som gör
 * fotografering i en källare möjlig.
 */
export const Fotorutnat = ({ children }: { readonly children: ReactNode }) => (
  <div className="photos">{children}</div>
);

export const Foto = ({ url, text }: { readonly url: string; readonly text: string }) => (
  <div className="ph">
    <img src={url} alt={text} />
  </div>
);

export const Laggtillfoto = ({
  onClick,
  etikett = 'Fota',
}: {
  readonly onClick: () => void;
  readonly etikett?: string;
}) => (
  <button type="button" className="ph add" onClick={onClick} aria-label={etikett}>
    <Ikon namn="kamera" storlek={22} />
  </button>
);

/** Genväg: ikon + text i en rad, för de små informationsraderna. */
export const Ikonrad = ({
  ikon,
  children,
}: {
  readonly ikon: Ikonnamn;
  readonly children: ReactNode;
}) => (
  <div className="rostrad">
    <Ikon namn={ikon} storlek={17} />
    <span>{children}</span>
  </div>
);
